import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { translations } from '../locales/translations';

const LanguageContext = createContext();

export const LANGUAGES = [
  { code: 'en', label: 'EN', name: 'English', primary: true },
  { code: 'ua', label: 'UA', name: 'Українська', primary: true },
  { code: 'ru', label: 'RU', name: 'Русский', primary: true },
  { code: 'es', label: 'ES', name: 'Español', primary: true },
  { code: 'de', label: 'DE', name: 'Deutsch', primary: false },
  { code: 'no', label: 'NO', name: 'Norsk', primary: false }
];

export const PRIMARY_LANGUAGES = LANGUAGES.filter(l => l.primary);
export const SECONDARY_LANGUAGES = LANGUAGES.filter(l => !l.primary);

export function LanguageProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract language from URL path
  const getLanguageFromPath = () => {
    const pathLang = location.pathname.split('/')[1];
    const validLang = LANGUAGES.find(l => l.code === pathLang);
    return validLang ? pathLang : null;
  };

  const [language, setLanguageState] = useState(() => {
    // Priority: URL > localStorage > browser > default
    const urlLang = getLanguageFromPath();
    if (urlLang) return urlLang;
    
    const saved = localStorage.getItem('dathent-language');
    if (saved && LANGUAGES.find(l => l.code === saved)) return saved;
    
    const browserLang = navigator.language.split('-')[0];
    if (LANGUAGES.find(l => l.code === browserLang)) return browserLang;
    
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('dathent-language', language);
    document.documentElement.lang = language;
  }, [language]);

  // Update URL when language changes
  const setLanguage = (newLang) => {
    setLanguageState(newLang);
    
    // Update URL path
    const currentPath = location.pathname;
    const currentLang = getLanguageFromPath();
    
    let newPath;
    if (currentLang) {
      // Replace existing language in path
      newPath = currentPath.replace(`/${currentLang}`, `/${newLang}`);
    } else {
      // Add language prefix
      newPath = `/${newLang}${currentPath}`;
    }
    
    // Keep search params and hash
    navigate(newPath + location.search + location.hash, { replace: true });
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
